import * as i0 from '@angular/core';
import { InjectionToken, Injectable, EventEmitter, booleanAttribute, forwardRef, Component, ChangeDetectionStrategy, ViewEncapsulation, Optional, Inject, Input, Output, ViewChild, NgModule } from '@angular/core';
import { SbbCommonModule } from '@sbb-esta/angular/core';
import * as i2 from '@sbb-esta/angular/icon';
import { SbbIconModule } from '@sbb-esta/angular/icon';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

const SBB_FILE_SELECTOR_OPTIONS = new InjectionToken('FILE_SELECTOR_OPTIONS');
const SBB_FILE_TYPES = {
    TEXT: ['text/*'],
    IMAGE: ['image/*'],
    IMAGE_JPG: ['image/jpeg', 'image/pjpeg'],
    IMAGE_PNG: ['image/png'],
    IMAGE_SVG: ['image/svg+xml'],
    IMAGE_GIF: ['image/gif'],
    AUDIO: ['audio/*'],
    AUDIO_MP4: ['audio/mp4'],
    AUDIO_MP3: ['audio/mpeg', 'audio/mp3'],
    AUDIO_OGG: ['audio/ogg'],
    AUDIO_WAVE: ['audio/wave'],
    AUDIO_WAV: ['audio/wav', 'audio/x-wav', 'audio/x-pn-wav', 'audio/webm'],
    VIDEO: ['video/*'],
    VIDEO_MP4: ['video/mp4'],
    VIDEO_MPEG: ['video/mpeg'],
    VIDEO_OGG: ['video/ogg'],
    VIDEO_MOV: ['video/quicktime'],
    VIDEO_WEBM: ['video/webm'],
    VIDEO_AVI: ['video/x-msvideo'],
    VIDEO_WMV: ['video/x-ms-wmv'],
    PDF: ['application/pdf'],
    MS_WORD_DOC: [
        'application/msword',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
        'application/vnd.ms-word.document.macroEnabled.12',
        'application/vnd.ms-word.template.macroEnabled.12',
    ],
    MS_EXCEL: [
        'application/vnd.ms-excel',
        'application/vnd.ms-excel',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'application/vnd.ms-excel.template.macroEnabled.12',
        'application/vnd.ms-excel.addin.macroEnabled.12',
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
    ],
    MS_POWERPOINT: [
        'application/vnd.ms-powerpoint',
        'application/vnd.ms-powerpoint',
        'application/vnd.ms-powerpoint',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.openxmlformats-officedocument.presentationml.template',
        'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
        'application/vnd.ms-powerpoint.addin.macroEnabled.12',
        'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
        'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
        'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
    ],
    ZIP: [
        'application/x-gtar',
        'application/x-gcompress',
        'application/compress',
        'application/x-tar',
        'application/x-rar-compressed',
        'application/octet-stream',
        'application/x-zip-compressed',
        'application/zip-compressed',
        'application/x-7z-compressed',
        'application/gzip',
        'application/zip',
        'application/x-bzip2',
    ],
};
const SBB_CATEGORY_MIME_TYPES = new Map()
    .set('doc', [
    ...SBB_FILE_TYPES.MS_WORD_DOC,
    ...SBB_FILE_TYPES.MS_EXCEL,
    ...SBB_FILE_TYPES.MS_POWERPOINT,
])
    .set('audio', [
    ...SBB_FILE_TYPES.AUDIO_MP4,
    ...SBB_FILE_TYPES.AUDIO_MP3,
    ...SBB_FILE_TYPES.AUDIO_OGG,
    ...SBB_FILE_TYPES.AUDIO_WAVE,
    ...SBB_FILE_TYPES.AUDIO_WAV,
])
    .set('image', [
    ...SBB_FILE_TYPES.IMAGE_GIF,
    ...SBB_FILE_TYPES.IMAGE_JPG,
    ...SBB_FILE_TYPES.IMAGE_PNG,
    ...SBB_FILE_TYPES.IMAGE_SVG,
])
    .set('pdf', SBB_FILE_TYPES.PDF)
    .set('video', [
    ...SBB_FILE_TYPES.VIDEO_MP4,
    ...SBB_FILE_TYPES.VIDEO_MPEG,
    ...SBB_FILE_TYPES.VIDEO_OGG,
    ...SBB_FILE_TYPES.VIDEO_MOV,
    ...SBB_FILE_TYPES.VIDEO_WEBM,
    ...SBB_FILE_TYPES.VIDEO_AVI,
    ...SBB_FILE_TYPES.VIDEO_WMV,
])
    .set('zip', SBB_FILE_TYPES.ZIP);

class SbbFileSelectorTypesService {
    /**
     * Returns the file type category/ies from given mime type.
     * @param mimeType Mime type of a file.
     * @returns File type category/ies.
     */
    getFileTypeCategoryByMimeType(mimeType) {
        return (Array.from(SBB_CATEGORY_MIME_TYPES).find(([_f, mimeTypes]) => mimeTypes.indexOf(mimeType) >= 0)?.[0] ?? 'generic');
    }
    /**
     * Returns all accepted formats.
     * @param typeCats File type category/ies.
     * @returns All formats accepted.
     */
    getAcceptString(...typeCats) {
        return typeCats
            .reduce((current, next) => current.concat(next), [])
            .map((t) => this._buildAcceptString(t))
            .filter((a) => !!a)
            .join(',');
    }
    /**
     * Returns the file extension from the given file name.
     * Returns empty string, if the file name has no extension or has the pattern '.name'.
     * @param fileName File name.
     * @return File with the name in input.
     */
    fileExtension(fileName) {
        const res = fileName.split('.');
        return res.length === 1 || (res.length === 2 && res[0] === '') ? '' : res.pop();
    }
    /**
     * Returns the file extension of the file with name in input.
     * @param fileName File name.
     * @returns File extension.
     */
    removeFileExtension(fileName) {
        const extension = this.fileExtension(fileName);
        return !extension ? fileName : fileName.substring(0, fileName.length - extension.length - 1);
    }
    /**
     * Returns the file size with the correct unit of measure.
     * @param bytes File size in bytes.
     * @returns File size with the correct unit of measure.
     */
    formatFileSize(bytes) {
        if (bytes === 0) {
            return '0 B';
        }
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
    }
    _buildAcceptString(fileTypeCategory) {
        switch (fileTypeCategory) {
            case 'doc':
                return SBB_FILE_TYPES.MS_WORD_DOC.concat(SBB_FILE_TYPES.MS_EXCEL, SBB_FILE_TYPES.MS_POWERPOINT).join(',');
            case 'image':
                return SBB_FILE_TYPES.IMAGE.join(',');
            case 'pdf':
                return SBB_FILE_TYPES.PDF.join(',');
            case 'video':
                return SBB_FILE_TYPES.VIDEO.join(',');
            case 'audio':
                return SBB_FILE_TYPES.AUDIO.join(',');
            case 'zip':
                return SBB_FILE_TYPES.ZIP.join(',');
            default:
                return '';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelectorTypesService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelectorTypesService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelectorTypesService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

// Workaround for: https://github.com/bazelbuild/rules_nodejs/issues/1265
let nextId = 0;
class SbbFileSelector {
    /** Id for the inner input field. */
    get inputId() {
        return `${this.id || this._uniqueId}-input`;
    }
    constructor(elementRef, _fileTypeService, _renderer, _changeDetector, options) {
        this._fileTypeService = _fileTypeService;
        this._renderer = _renderer;
        this._changeDetector = _changeDetector;
        this._labelUploadFile = $localize `:Button label to select files for upload@@sbbFileSelectorUploadFile:Upload file`;
        this._labelRemoveFile = $localize `:Hidden button label to remove a file from the selection list@@sbbFileSelectorRemoveFile:Remove file`;
        this._uniqueId = `sbb-file-selector-${++nextId}`;
        /** Whether the file selector is disabled. */
        this.disabled = false;
        /** Unique id of the element. */
        this.id = this._uniqueId;
        /** Mode on file selector component to chose more files to upload. */
        this.multiple = false;
        /** Set if the component should add files on top of the already selected ones or keep default input file behaviour. */
        this.multipleMode = 'default';
        /** Event emitted to a change on file selector component (for example the uploading of files). */
        this.fileChanged = new EventEmitter();
        /** Property that listens changes on file selector. */
        this._onChange = (_) => { };
        /** Property that catches the interaction with user. */
        this._onTouched = () => { };
        if (options) {
            this.accept = options.accept;
            this.capture = options.capture;
            this.multiple = !!options.multiple;
            this.multipleMode = options.multipleMode || this.multipleMode;
        }
    }
    /**
     * Applies changes on the list of files uploaded.
     * @param files Files uploaded.
     */
    fileChange(files) {
        if (!files) {
            return;
        }
        if (files instanceof FileList) {
            this.applyChanges(Array.from(files));
            return;
        }
        const target = files.target;
        if (target && target.files) {
            this.applyChanges(Array.from(target.files));
        }
    }
    /**
     * Applies changes on sbb-file-selector.
     * @param files Files uploaded.
     * @param action add or remove
     */
    applyChanges(files, action = 'add') {
        const filesToAdd = action === 'add' ? this._getFileListByMode(files) : files;
        this._renderer.setProperty(this.fileInput.nativeElement, 'value', null);
        this._onChange(filesToAdd);
        this.writeValue(filesToAdd);
        this.fileChanged.emit(filesToAdd);
    }
    /**
     * Returns the list of files without the file deleted.
     * @param file File to delete.
     * @returns List of files without the file deleted.
     */
    removeFile(file) {
        const filteredList = this.filesList.filter((f) => !this._checkFileEquality(f, file));
        this.applyChanges(filteredList, 'remove');
    }
    // Implemented as part of ControlValueAccessor.
    writeValue(value) {
        this.filesList = value;
        this._changeDetector.detectChanges();
    }
    // Implemented as part of ControlValueAccessor.
    registerOnChange(fn) {
        this._onChange = fn;
    }
    // Implemented as part of ControlValueAccessor.
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    // Implemented as part of ControlValueAccessor.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this._changeDetector.markForCheck();
    }
    /** Returns the file type category by the file type in input. */
    _resolveFileTypeCategory(file) {
        return this._fileTypeService.getFileTypeCategoryByMimeType(file.type);
    }
    /** Get the file name without the extension. */
    _fileNameNoExtension(file) {
        return this._fileTypeService.removeFileExtension(file.name);
    }
    /** Get the file extension of the given file. */
    _fileExtension(file) {
        return this._fileTypeService.fileExtension(file.name);
    }
    /** Returns the formatted file size of the given file. */
    _fileSizeFormatted(file) {
        return this._fileTypeService.formatFileSize(file.size);
    }
    _getFileListByMode(incomingFiles) {
        if (this.multiple && this.multipleMode === 'persistent') {
            return incomingFiles
                .filter((f) => this.filesList.findIndex((flItem) => this._checkFileEquality(f, flItem)) === -1)
                .concat(this.filesList);
        }
        return incomingFiles;
    }
    _checkFileEquality(file1, file2) {
        return (file1.name === file2.name &&
            file1.size === file2.size &&
            file1.lastModified === file2.lastModified);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelector, deps: [{ token: i0.ElementRef }, { token: SbbFileSelectorTypesService }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: SBB_FILE_SELECTOR_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.7", type: SbbFileSelector, isStandalone: true, selector: "sbb-file-selector", inputs: { disabled: ["disabled", "disabled", booleanAttribute], id: "id", accept: "accept", capture: "capture", multiple: ["multiple", "multiple", booleanAttribute], multipleMode: "multipleMode" }, outputs: { fileChanged: "fileChanged" }, host: { properties: { "attr.id": "this.id" }, classAttribute: "sbb-file-selector" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SbbFileSelector),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "fileInput", first: true, predicate: ["fileInput"], descendants: true, static: true }], exportAs: ["sbbFileSelector"], ngImport: i0, template: "<label\n  class=\"sbb-file-selector-label sbb-secondary-button\"\n  [class.sbb-disabled]=\"disabled\"\n  [for]=\"inputId\"\n>\n  <input\n    type=\"file\"\n    class=\"cdk-visually-hidden\"\n    #fileInput\n    [attr.id]=\"inputId\"\n    [attr.accept]=\"accept\"\n    [attr.capture]=\"capture\"\n    [attr.multiple]=\"multiple || null\"\n    [disabled]=\"disabled\"\n    (change)=\"fileChange($event)\"\n    (blur)=\"_onTouched()\"\n  />\n  {{ _labelUploadFile }}\n  <span class=\"sbb-file-selector-upload-icon\">\n    <sbb-icon class=\"sbb-icon-scaled\" svgIcon=\"upload-small\"></sbb-icon>\n  </span>\n</label>\n\n@if (filesList && filesList.length) {\n  <ul class=\"sbb-file-selector-list sbb-clean-list sbb-icon-scaled\">\n    @for (file of filesList; track file) {\n      <li>\n        <div role=\"presentation\" class=\"sbb-file-selector-list-type-icon\">\n          @switch (_resolveFileTypeCategory(file)) {\n            @case ('doc') {\n              <sbb-icon svgIcon=\"document-text-small\"></sbb-icon>\n            }\n            @case ('image') {\n              <sbb-icon svgIcon=\"document-image-small\"></sbb-icon>\n            }\n            @case ('pdf') {\n              <sbb-icon svgIcon=\"document-pdf-small\"></sbb-icon>\n            }\n            @case ('audio') {\n              <sbb-icon svgIcon=\"document-sound-small\"></sbb-icon>\n            }\n            @case ('video') {\n              <sbb-icon svgIcon=\"document-video-small\"></sbb-icon>\n            }\n            @case ('zip') {\n              <sbb-icon svgIcon=\"document-zip-small\"></sbb-icon>\n            }\n            @default {\n              <sbb-icon svgIcon=\"document-standard-small\"></sbb-icon>\n            }\n          }\n        </div>\n        <span class=\"sbb-file-selector-list-desc\"\n          >{{ _fileNameNoExtension(file) }} (<span class=\"sbb-file-selector-list-file-ext\">{{\n            _fileExtension(file)\n          }}</span\n          >, {{ _fileSizeFormatted(file) }})</span\n        >\n        <button\n          type=\"button\"\n          class=\"sbb-file-selector-list-remove-icon sbb-button-reset-frameless\"\n          (click)=\"removeFile(file)\"\n        >\n          <span class=\"cdk-visually-hidden\">{{ _labelRemoveFile }}</span>\n          <sbb-icon svgIcon=\"trash-small\"></sbb-icon>\n        </button>\n      </li>\n    }\n  </ul>\n}\n", styles: [".sbb-file-selector{display:inline-block}.sbb-file-selector-label{display:inline-flex}.sbb-file-selector-upload-icon{display:inline-block;margin-left:calc(0.4rem*var(--sbb-scaling-factor));margin-bottom:-0.5em}.sbb-disabled .sbb-file-selector-upload-icon{display:none}:where(html:not(.sbb-lean)) .sbb-file-selector-upload-icon{margin-top:var(--sbb-scaling-factor)}.sbb-file-selector-list{font-size:var(--sbb-font-size);line-height:var(--sbb-line-height);margin:var(--sbb-divider-small) 0 0}.sbb-file-selector-list li{margin-top:var(--sbb-divider-thin);display:flex;align-items:center}:where(html.sbb-lean) .sbb-file-selector-list{margin-top:var(--sbb-divider-medium)}:is(.sbb-file-selector-list-type-icon,.sbb-file-selector-list-remove-icon){display:inline-flex}.sbb-file-selector-list-type-icon{margin-right:calc(0.5333333333rem*var(--sbb-scaling-factor))}:where(html.sbb-lean) .sbb-file-selector-list-type-icon{vertical-align:top}.sbb-file-selector-list-desc{text-decoration:underline}.sbb-file-selector-list-remove-icon{position:relative;width:calc(2.1333333333rem*var(--sbb-scaling-factor));height:calc(2.1333333333rem*var(--sbb-scaling-factor));margin-left:calc(0.5333333333rem*var(--sbb-scaling-factor));cursor:pointer;outline:0;color:var(--sbb-file-selector-list-remove-icon-color);border:var(--sbb-border-width-thin) solid var(--sbb-color-storm);border-radius:50%;padding:calc(0.2rem*var(--sbb-scaling-factor))}.sbb-file-selector-list-remove-icon:is(:hover,:focus,:active){color:var(--sbb-color-call-to-action-hover);border-color:var(--sbb-color-call-to-action-hover)}:where(html.sbb-lean) .sbb-file-selector-list-remove-icon{width:var(--sbb-icon-size-default);height:var(--sbb-icon-size-default);vertical-align:top;border:none;padding:0}.sbb-file-selector-list-file-ext{text-transform:uppercase}"], dependencies: [{ kind: "ngmodule", type: SbbIconModule }, { kind: "component", type: i2.SbbIcon, selector: "sbb-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon", "id"], exportAs: ["sbbIcon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelector, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-file-selector', exportAs: 'sbbFileSelector', host: {
                        class: 'sbb-file-selector',
                        '[attr.id]': 'this.id',
                    }, inputs: ['disabled'], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SbbFileSelector),
                            multi: true,
                        },
                    ], standalone: true, imports: [SbbIconModule], template: "<label\n  class=\"sbb-file-selector-label sbb-secondary-button\"\n  [class.sbb-disabled]=\"disabled\"\n  [for]=\"inputId\"\n>\n  <input\n    type=\"file\"\n    class=\"cdk-visually-hidden\"\n    #fileInput\n    [attr.id]=\"inputId\"\n    [attr.accept]=\"accept\"\n    [attr.capture]=\"capture\"\n    [attr.multiple]=\"multiple || null\"\n    [disabled]=\"disabled\"\n    (change)=\"fileChange($event)\"\n    (blur)=\"_onTouched()\"\n  />\n  {{ _labelUploadFile }}\n  <span class=\"sbb-file-selector-upload-icon\">\n    <sbb-icon class=\"sbb-icon-scaled\" svgIcon=\"upload-small\"></sbb-icon>\n  </span>\n</label>\n\n@if (filesList && filesList.length) {\n  <ul class=\"sbb-file-selector-list sbb-clean-list sbb-icon-scaled\">\n    @for (file of filesList; track file) {\n      <li>\n        <div role=\"presentation\" class=\"sbb-file-selector-list-type-icon\">\n          @switch (_resolveFileTypeCategory(file)) {\n            @case ('doc') {\n              <sbb-icon svgIcon=\"document-text-small\"></sbb-icon>\n            }\n            @case ('image') {\n              <sbb-icon svgIcon=\"document-image-small\"></sbb-icon>\n            }\n            @case ('pdf') {\n              <sbb-icon svgIcon=\"document-pdf-small\"></sbb-icon>\n            }\n            @case ('audio') {\n              <sbb-icon svgIcon=\"document-sound-small\"></sbb-icon>\n            }\n            @case ('video') {\n              <sbb-icon svgIcon=\"document-video-small\"></sbb-icon>\n            }\n            @case ('zip') {\n              <sbb-icon svgIcon=\"document-zip-small\"></sbb-icon>\n            }\n            @default {\n              <sbb-icon svgIcon=\"document-standard-small\"></sbb-icon>\n            }\n          }\n        </div>\n        <span class=\"sbb-file-selector-list-desc\"\n          >{{ _fileNameNoExtension(file) }} (<span class=\"sbb-file-selector-list-file-ext\">{{\n            _fileExtension(file)\n          }}</span\n          >, {{ _fileSizeFormatted(file) }})</span\n        >\n        <button\n          type=\"button\"\n          class=\"sbb-file-selector-list-remove-icon sbb-button-reset-frameless\"\n          (click)=\"removeFile(file)\"\n        >\n          <span class=\"cdk-visually-hidden\">{{ _labelRemoveFile }}</span>\n          <sbb-icon svgIcon=\"trash-small\"></sbb-icon>\n        </button>\n      </li>\n    }\n  </ul>\n}\n", styles: [".sbb-file-selector{display:inline-block}.sbb-file-selector-label{display:inline-flex}.sbb-file-selector-upload-icon{display:inline-block;margin-left:calc(0.4rem*var(--sbb-scaling-factor));margin-bottom:-0.5em}.sbb-disabled .sbb-file-selector-upload-icon{display:none}:where(html:not(.sbb-lean)) .sbb-file-selector-upload-icon{margin-top:var(--sbb-scaling-factor)}.sbb-file-selector-list{font-size:var(--sbb-font-size);line-height:var(--sbb-line-height);margin:var(--sbb-divider-small) 0 0}.sbb-file-selector-list li{margin-top:var(--sbb-divider-thin);display:flex;align-items:center}:where(html.sbb-lean) .sbb-file-selector-list{margin-top:var(--sbb-divider-medium)}:is(.sbb-file-selector-list-type-icon,.sbb-file-selector-list-remove-icon){display:inline-flex}.sbb-file-selector-list-type-icon{margin-right:calc(0.5333333333rem*var(--sbb-scaling-factor))}:where(html.sbb-lean) .sbb-file-selector-list-type-icon{vertical-align:top}.sbb-file-selector-list-desc{text-decoration:underline}.sbb-file-selector-list-remove-icon{position:relative;width:calc(2.1333333333rem*var(--sbb-scaling-factor));height:calc(2.1333333333rem*var(--sbb-scaling-factor));margin-left:calc(0.5333333333rem*var(--sbb-scaling-factor));cursor:pointer;outline:0;color:var(--sbb-file-selector-list-remove-icon-color);border:var(--sbb-border-width-thin) solid var(--sbb-color-storm);border-radius:50%;padding:calc(0.2rem*var(--sbb-scaling-factor))}.sbb-file-selector-list-remove-icon:is(:hover,:focus,:active){color:var(--sbb-color-call-to-action-hover);border-color:var(--sbb-color-call-to-action-hover)}:where(html.sbb-lean) .sbb-file-selector-list-remove-icon{width:var(--sbb-icon-size-default);height:var(--sbb-icon-size-default);vertical-align:top;border:none;padding:0}.sbb-file-selector-list-file-ext{text-transform:uppercase}"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: SbbFileSelectorTypesService }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SBB_FILE_SELECTOR_OPTIONS]
                }] }], propDecorators: { disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], id: [{
                type: Input
            }], accept: [{
                type: Input
            }], capture: [{
                type: Input
            }], multiple: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], multipleMode: [{
                type: Input
            }], fileChanged: [{
                type: Output
            }], fileInput: [{
                type: ViewChild,
                args: ['fileInput', { static: true }]
            }] } });

class SbbFileSelectorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelectorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelectorModule, imports: [SbbCommonModule, SbbIconModule, SbbFileSelector], exports: [SbbFileSelector] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelectorModule, imports: [SbbCommonModule, SbbIconModule, SbbFileSelector] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbFileSelectorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [SbbCommonModule, SbbIconModule, SbbFileSelector],
                    exports: [SbbFileSelector],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SBB_CATEGORY_MIME_TYPES, SBB_FILE_SELECTOR_OPTIONS, SBB_FILE_TYPES, SbbFileSelector, SbbFileSelectorModule, SbbFileSelectorTypesService };
